import React from "react";
import { Grid } from "@material-ui/core";
import styles from "./styles.module.scss";
import { Link } from "react-router-dom";
import Model from "../../../../types/Model";
import { imageCompressConstants } from "../../../../constants/imageCompressConstants";
import { formatterPrice } from "../../../../utils/app.helpers/price.formatter";
import classNames from "classnames";
import { buildContentfulUrl } from "../../../../utils/contentfulImage.helper/contentfulImage";

// Map an EH chair's `serial` value to its variant badge image.
// Add new entries here when new EH sub-products launch.
const EH_VARIANT_LOGOS: Record<string, string> = {
  "EH-5": "/assets/images/eh/eh5_light.png",
  "EH-5+": "/assets/images/eh/eh5plus_light.png",
  "EH-Q": "/assets/images/eh/ehQ_light.png",
};

const resolveEhVariantLogo = (serial?: string): string | null => {
  if (!serial) return null;
  const raw = serial.trim();
  if (EH_VARIANT_LOGOS[raw]) return EH_VARIANT_LOGOS[raw];
  // Tolerate minor formatting differences: "EH5", "eh 5+", "ehq" etc.
  const stripped = raw.replace(/[\s_-]/g, "").toUpperCase();
  if (stripped === "EH5") return EH_VARIANT_LOGOS["EH-5"];
  if (stripped === "EH5+") return EH_VARIANT_LOGOS["EH-5+"];
  if (stripped === "EHQ") return EH_VARIANT_LOGOS["EH-Q"];
  return null;
};

interface IProductCard {
  model: Model;
  link?: string;
  isDiscounted?: boolean;
  isCompareMode?: boolean;
  isSelected?: boolean;
  setCompareArray?: Function;
  compareArray?: Model[];
  compareModalOpen?: boolean;
  category?: string;
}

const ProductCard = ({
  model,
  link,
  isDiscounted,
  isCompareMode,
  isSelected,
  setCompareArray,
  compareArray,
  compareModalOpen,
  category = '',
}: IProductCard) => {
  
  const frontImgSrc = buildContentfulUrl(
    model.angle_thumbnail,
    imageCompressConstants.productCardThumb
  );

  const backImgSrc = buildContentfulUrl(
    model.thumbnail,
    imageCompressConstants.productCardThumb
  );
  return (
    <Grid
      key={model.id}
      className={classNames(
        styles.card,
        compareArray && compareArray.length !== 0 ? styles.activeMode : ""
      )}
    >
      <div
        className={classNames(
          styles.selector,
          !isCompareMode ? styles.selector_disabled : "",
          isSelected ? styles.selector_active : ""
        )}
      >
        <div className={styles.selector_dot} />
        <span className={styles.selector_compare}>Compare</span>
      </div>
      {(isCompareMode && !compareModalOpen) && (
        <div
          className={styles.overlay}
          onClick={(e) => {
            e.stopPropagation();
            setCompareArray((prev: Model[]) => {
              if (prev.some((selected) => selected.id === model.id)) {
                return prev.filter((selected) => selected.id !== model.id);
              } else {
                return [...prev, model];
              }
            });
          }}
        />
      )}
      <Link
        className={styles.content}
        key={model.id}
        to={
          link && !isDiscounted
            ? link
            : `/${model.category.toLowerCase()}/${model.title
                .split(" ")
                .join("_")
                .toLowerCase()}`
        }
      >
        <div className={styles.front_side}>
          <img
            className={styles.preview}
            src={frontImgSrc}
            alt=""
          />
        </div>

        <div className={styles.back_side}>
          <img
            className={styles.preview}
            src={backImgSrc}
            alt=""
          />
        </div>

        <p
          className={
            model.title && model.title.includes("iOO")
              ? styles.title_ioo
              : styles.title
          }
        >
          <span>{category ? category : model.category}</span>
          {model.title.includes("-")
            ? model.title.split("-").join("/")
            : model.title}
          {model.price > 0 && (
            model.isDiscounted ? (
              <span className={styles.sale_container}>
                <b className={styles.card_price_old}>
                  {formatterPrice(model.price)}
                </b>
                <b className={styles.card_price}>
                  {formatterPrice(
                    model.price * ((100 - model.discountValue) / 100)
                  )}
                </b>
              </span>
            ) : (
              <b className={styles.card_price}>{formatterPrice(model.price)}</b>
            )
          )}
        </p>
      </Link>

      <div className={styles.logo_container}>
        {model.isDiscounted && (
          <div className={styles.logo_container_sale}>
            - {model.discountValue} %
          </div>
        )}
        {model.isFabrix && (
          <div className={styles.logo_container__fabrix}>
            <img src={"/assets/images/fabrix_icon/fabrix_logo.webp"} alt="" />
          </div>
        )}
        {model.gsa && (
          <div className={styles.logo_container__gsa}>
            <img src={"/assets/images/gsa_icon/gsa_logo.png"} alt="" />
          </div>
        )}
        {(() => {
          const ehLogo =
            model.category &&
            model.category.toLowerCase() === "eh"
              ? resolveEhVariantLogo(model.serial)
              : null;
          return ehLogo ? (
            <div className={styles.logo_container__eh}>
              <img src={ehLogo} alt={model.serial || "EH"} />
            </div>
          ) : null;
        })()}
      </div>
    </Grid>
  );
};

export default ProductCard;
